"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getGanttVisibleColumns = exports.getGanttStyle = exports.getCalendarVisibleColumnCount = exports.getOrgChartVisibleColumns = exports.getCalendarVisibleColumns = exports.getKanbanViewStatus = exports.getOrgChartViewStatus = exports.getCalendarViewStatus = exports.getCalendarStyle = exports.getQueryMeta = exports.getKanbanGroupMapIds = exports.getFilterConditionValue = exports.getActualRowCount = exports.getViewRowHeight = exports.getPermissions = exports.getCurrentGalleryViewStyle = exports.getGroupLevel = exports.getRowHeightFromLevel = exports.getColumnByFieldId = exports.getEffectConditionCount = exports.getVisibleColumnCount = exports.getRecordSnapshot = exports.getRangeFields = exports.getActiveViewGroupInfo = exports.getActiveViewSortInfo = exports.getActiveViewFilterInfo = exports.getFrozenColumns = exports.getExceptFrozenColumns = exports.findColumnIndexById = exports.getVisibleColumnsMap = exports.getVisibleColumns = exports.getColumnIndexMap = exports.getFieldMapIgnorePermission = exports.getFieldMap = exports.getFieldMapBase = exports.getGroupInfoWithPermission = exports.getKanbanFieldId = exports.getFilterInfoExceptInvalid = exports.getTemporaryView = exports.getViewByIdWithDefault = exports.getCurrentView = exports.getCurrentViewBase = exports.getViewIndex = exports.getView = exports.getColumnWidth = exports.getVisibleColumnsBase = exports.getCurrentSearchRecordId = exports.getCurrentSearchItem = exports.getDatasheetIds = exports.createDeepEqualSelector = void 0;
exports.getDateTimeCellAlarmForClient = exports.getDateTimeCellAlarm = exports.integrateRecordEditable = exports.getGanttVisibleColumnCount = void 0;
const engine_1 = require("../../../../../../engine");
const i18n_1 = require("../../../../../../exports/i18n");
const isEqual_1 = __importDefault(require("lodash/isEqual"));
const isNumber_1 = __importDefault(require("lodash/isNumber"));
const model_1 = require("../../../../../../model");
const re_reselect_1 = __importDefault(require("re-reselect"));
const reselect_1 = require("reselect");
const constants_1 = require("../../../../../shared/store/constants");
const interfaces_1 = require("../../../../../../exports/store/interfaces");
const mirror_1 = require("../../../../../../modules/database/store/selectors/resource/mirror");
const types_1 = require("../../../../../../types");
const base_1 = require("./base");
const rows_calc_1 = require("./rows_calc");
exports.createDeepEqualSelector = (0, reselect_1.createSelectorCreator)(reselect_1.defaultMemoize, isEqual_1.default);
const defaultKeySelector = (state, datasheetId) => datasheetId || (0, base_1.getActiveDatasheetId)(state);
exports.getDatasheetIds = (0, exports.createDeepEqualSelector)((state) => Object.keys(state.datasheetMap), keys => keys);
/**
 * get current search result's one record(cell)
 * @param state
 * @returns
 */
const getCurrentSearchItem = (state) => {
    const searchKeyword = (0, base_1.getSearchKeyword)(state);
    const searchResultCursorIndex = (0, base_1.getSearchResultCursorIndex)(state);
    if (!searchKeyword) {
        return;
    }
    const searchResultArray = (0, rows_calc_1.getSearchResult)(state);
    if (!searchResultArray || !Array.isArray(searchResultArray)) {
        return;
    }
    const currentItem = searchResultArray[searchResultCursorIndex || 0];
    return currentItem;
};
exports.getCurrentSearchItem = getCurrentSearchItem;
const getCurrentSearchRecordId = (state) => {
    var _a;
    const searchKeyword = (0, base_1.getSearchKeyword)(state);
    if (!searchKeyword) {
        return;
    }
    const searchResultCursorIndex = (0, base_1.getSearchResultCursorIndex)(state);
    const visibleRows = (0, rows_calc_1.getVisibleRows)(state);
    // Compatible with the case where the search result visibleRows may be empty.
    if (searchResultCursorIndex != null && visibleRows.length) {
        return (_a = visibleRows[searchResultCursorIndex]) === null || _a === void 0 ? void 0 : _a.recordId;
    }
    return;
};
exports.getCurrentSearchRecordId = getCurrentSearchRecordId;
// (no cache)calc and get current visible columns
const getVisibleColumnsBase = (view) => {
    return view ? view.columns.filter((item, i) => !(item.hidden && i !== 0)) : [];
};
exports.getVisibleColumnsBase = getVisibleColumnsBase;
// calc and get a width of a column
const getColumnWidth = (column) => (!column || column.width == null ? constants_1.DEFAULT_COLUMN_WIDTH : column.width);
exports.getColumnWidth = getColumnWidth;
const getView = (snapshot, viewId) => {
    return snapshot.meta.views.find(view => view.id === viewId);
};
exports.getView = getView;
const getViewIndex = (snapshot, viewId) => {
    return snapshot.meta.views.findIndex(view => view.id === viewId);
};
exports.getViewIndex = getViewIndex;
const filterColumnsByPermission = (columns, fieldPermissionMap) => {
    return columns.filter(column => {
        // TODO: column permission delete this logic (2nd phase)
        const fieldRole = (0, base_1.getFieldRoleByFieldId)(fieldPermissionMap, column.fieldId);
        return fieldRole !== interfaces_1.Role.None;
    });
};
const getCurrentViewBase = (snapshot, viewId, datasheetId, fieldPermissionMap, mirror) => {
    if (!viewId) {
        return;
    }
    const view = (0, exports.getTemporaryView)(snapshot, viewId, datasheetId, mirror);
    if (!view) {
        return;
    }
    const permissionColumns = filterColumnsByPermission(view.columns, fieldPermissionMap);
    if (permissionColumns.length !== view.columns.length) {
        return Object.assign(Object.assign({}, view), { columns: permissionColumns });
    }
    return view;
};
exports.getCurrentViewBase = getCurrentViewBase;
exports.getCurrentView = (0, re_reselect_1.default)([
    base_1.getSnapshot,
    base_1.getActiveViewId,
    (state, datasheetId) => datasheetId || (0, base_1.getActiveDatasheetId)(state),
    base_1.getFieldPermissionMap,
    (state) => (0, mirror_1.getMirror)(state),
], exports.getCurrentViewBase)({
    // keySelector: (state, datasheetId) => state.pageParams.mirrorId || datasheetId || getActiveDatasheetId(state),
    keySelector: defaultKeySelector,
});
const getViewByIdWithDefault = (state, datasheetId, viewId) => {
    const snapshot = (0, base_1.getSnapshot)(state, datasheetId);
    const fieldPermissionMap = (0, base_1.getFieldPermissionMap)(state, datasheetId);
    if (!snapshot) {
        return null;
    }
    const firstViewId = snapshot.meta.views[0].id;
    let defaultView = (0, exports.getCurrentViewBase)(snapshot, firstViewId, datasheetId, fieldPermissionMap, (0, mirror_1.getMirror)(state));
    if (viewId) {
        defaultView = (0, exports.getCurrentViewBase)(snapshot, viewId, datasheetId, fieldPermissionMap, (0, mirror_1.getMirror)(state)) || defaultView;
    }
    return defaultView;
};
exports.getViewByIdWithDefault = getViewByIdWithDefault;
const getTemporaryView = (snapshot, viewId, _datasheetId, mirror) => {
    const temporaryView = mirror === null || mirror === void 0 ? void 0 : mirror.temporaryView;
    if (!snapshot) {
        return;
    }
    const originView = (0, base_1.getViewById)(snapshot, viewId);
    if (!temporaryView || (mirror === null || mirror === void 0 ? void 0 : mirror.sourceInfo.datasheetId) !== snapshot.datasheetId) {
        return originView;
    }
    if (originView && originView.columns && temporaryView && temporaryView.columns) {
        const viewFieldIds = new Set(originView.columns.map(column => column.fieldId));
        // 创建一个新的temp列数组，只包含在view中的列
        temporaryView.columns = temporaryView.columns.filter(column => viewFieldIds.has(column.fieldId));
        // 向temp添加那些在view中但在temp中不存在的列
        originView.columns.forEach(column => {
            if (!temporaryView.columns.some(tempColumn => tempColumn.fieldId === column.fieldId)) {
                temporaryView.columns.push(Object.assign({}, column)); // 使用扩展运算符复制对象
            }
        });
    }
    // in mirror, if any view config is modified,
    // the original table's view config will not affect the mirror, so here directly use the mirror's cache data
    return Object.assign({ id: originView === null || originView === void 0 ? void 0 : originView.id, type: originView === null || originView === void 0 ? void 0 : originView.type, rows: originView === null || originView === void 0 ? void 0 : originView.rows }, temporaryView);
};
exports.getTemporaryView = getTemporaryView;
const getFilterInfoExceptInvalid = (state, datasheetId, filterInfo) => {
    // filterInfo.conditions is empty will cause no filter but no data returned, so we need to filter it
    if (!filterInfo || !filterInfo.conditions.length) {
        return undefined;
    }
    const fieldPermissionMap = (0, base_1.getFieldPermissionMap)(state, datasheetId);
    const snapshot = (0, base_1.getSnapshot)(state, datasheetId);
    if (!fieldPermissionMap || !snapshot) {
        return filterInfo;
    }
    return Object.assign(Object.assign({}, filterInfo), { conditions: filterInfo.conditions.filter(condition => {
            const fieldMap = snapshot.meta.fieldMap;
            const field = fieldMap[condition.fieldId];
            if (!field) {
                return false;
            }
            return true;
        }) });
};
exports.getFilterInfoExceptInvalid = getFilterInfoExceptInvalid;
const getKanbanFieldId = (state) => {
    const view = (0, exports.getCurrentView)(state);
    const fieldMap = (0, exports.getFieldMap)(state);
    if (!view || view.type !== constants_1.ViewType.Kanban || !fieldMap) {
        return;
    }
    const fieldId = view.style.kanbanFieldId;
    if (fieldId && !fieldMap[fieldId]) {
        return;
    }
    return fieldId;
};
exports.getKanbanFieldId = getKanbanFieldId;
const getGroupInfoWithPermission = (state, groupInfo, datasheetId) => {
    const fieldPermissionMap = (0, base_1.getFieldPermissionMap)(state, datasheetId);
    if (!groupInfo || !fieldPermissionMap) {
        return groupInfo;
    }
    return groupInfo.filter(group => {
        const fieldRole = (0, base_1.getFieldRoleByFieldId)(fieldPermissionMap, group.fieldId);
        if (fieldRole === interfaces_1.Role.None) {
            return false;
        }
        return true;
    });
};
exports.getGroupInfoWithPermission = getGroupInfoWithPermission;
// TODO: field permissions 2nd version, no need to handle special case if there is data filter
const getFieldMapBase = (datasheet, fieldPermissionMap) => {
    const fieldMap = datasheet && datasheet.snapshot.meta.fieldMap;
    if (!fieldPermissionMap) {
        return fieldMap;
    }
    if (!fieldMap) {
        return null;
    }
    const _fieldMap = {};
    for (const k in fieldMap) {
        const fieldRole = (0, base_1.getFieldRoleByFieldId)(fieldPermissionMap, k);
        if (fieldRole === interfaces_1.Role.None) {
            // room-server currently don't do data filter for no permission fields,
            // so do it in selector
            continue;
        }
        _fieldMap[k] = fieldMap[k];
    }
    return _fieldMap;
};
exports.getFieldMapBase = getFieldMapBase;
exports.getFieldMap = (0, re_reselect_1.default)([base_1.getDatasheet, base_1.getFieldPermissionMap], exports.getFieldMapBase)(defaultKeySelector);
exports.getFieldMapIgnorePermission = (0, re_reselect_1.default)([base_1.getDatasheet], exports.getFieldMapBase)(defaultKeySelector);
exports.getColumnIndexMap = (0, reselect_1.createSelector)([exports.getCurrentView], view => {
    const columnsMap = {};
    if (!view) {
        return columnsMap;
    }
    for (const [k, v] of view.columns.entries()) {
        columnsMap[v.fieldId] = k;
    }
    return columnsMap;
});
exports.getVisibleColumns = (0, re_reselect_1.default)([exports.getCurrentView, base_1.getFieldPermissionMap], (view, fieldPermissionMap) => {
    // ignore the first column as hidden
    return view
        ? view.columns.filter((item, i) => {
            const fieldRole = (0, base_1.getFieldRoleByFieldId)(fieldPermissionMap, item.fieldId);
            if (fieldRole === interfaces_1.Role.None) {
                return false;
            }
            return !(item.hidden && i !== 0);
        })
        : [];
})(defaultKeySelector);
exports.getVisibleColumnsMap = (0, reselect_1.createSelector)([exports.getVisibleColumns], columns => {
    return new Map(columns.map((item, index) => [item.fieldId, index]));
});
const findColumnIndexById = (state, id) => {
    const columnsMap = (0, exports.getVisibleColumnsMap)(state);
    const index = columnsMap.get(id);
    if (!(0, isNumber_1.default)(index)) {
        throw new Error((0, i18n_1.t)(i18n_1.Strings.error_not_exist_id, {
            id,
        }));
    }
    return index;
};
exports.findColumnIndexById = findColumnIndexById;
// reselect for non-frozen columns
exports.getExceptFrozenColumns = (0, reselect_1.createSelector)([exports.getVisibleColumns, exports.getCurrentView], (columns, view) => {
    return !view || (view.type !== constants_1.ViewType.Grid && view.type !== constants_1.ViewType.Gantt) ? [] : columns.slice(view.frozenColumnCount);
});
// reselect for frozen columns
exports.getFrozenColumns = (0, reselect_1.createSelector)([exports.getVisibleColumns, exports.getCurrentView], (columns, view) => {
    return !view || (view.type !== constants_1.ViewType.Grid && view.type !== constants_1.ViewType.Gantt) ? [] : columns.slice(0, view.frozenColumnCount);
});
// get current active view's filter info
exports.getActiveViewFilterInfo = (0, reselect_1.createSelector)([exports.getCurrentView], (view) => {
    if (!(view === null || view === void 0 ? void 0 : view.filterInfo)) {
        return null;
    }
    return view.filterInfo;
});
// get current active view's sort info
exports.getActiveViewSortInfo = (0, reselect_1.createSelector)([exports.getCurrentView], (view) => {
    return view === null || view === void 0 ? void 0 : view.sortInfo;
});
// get current active view's group info
exports.getActiveViewGroupInfo = (0, reselect_1.createSelector)([exports.getCurrentView], (view) => {
    if (!(view === null || view === void 0 ? void 0 : view.groupInfo)) {
        return [];
    }
    return view.groupInfo;
});
const getRangeFields = (state, range, datasheetId) => {
    const rangeIndex = model_1.Range.bindModel(range).getIndexRange(state);
    if (!rangeIndex) {
        return null;
    }
    const columnSlice = [rangeIndex.field.min, rangeIndex.field.max + 1];
    const columns = (0, exports.getVisibleColumns)(state);
    return columns.slice(...columnSlice).map(col => (0, base_1.getField)(state, col.fieldId, datasheetId));
};
exports.getRangeFields = getRangeFields;
const getRecordSnapshot = (state, datasheetId, recordId) => {
    const snapshot = (0, base_1.getSnapshot)(state, datasheetId);
    const fieldMap = (0, exports.getFieldMap)(state, datasheetId);
    if (!snapshot || !fieldMap) {
        return null;
    }
    const record = (0, base_1.getRecord)(state, recordId, datasheetId);
    if (!record) {
        return null;
    }
    return {
        meta: { fieldMap },
        recordMap: {
            [recordId]: record,
        },
        datasheetId,
    };
};
exports.getRecordSnapshot = getRecordSnapshot;
const getVisibleColumnCount = (state) => {
    const view = (0, exports.getCurrentView)(state);
    if (!view) {
        return -1;
    }
    return (0, exports.getVisibleColumns)(state).length;
};
exports.getVisibleColumnCount = getVisibleColumnCount;
/**
 * get the number of filters that "have been applied"
 */
const getEffectConditionCount = (state) => {
    const view = (0, exports.getCurrentView)(state);
    if (!view) {
        return 0;
    }
    const filterInfo = view.filterInfo;
    if (!filterInfo) {
        return 0;
    }
    // TODO improve the logic
    return filterInfo.conditions.length; // filter(isConditionTakeEffect).length;
};
exports.getEffectConditionCount = getEffectConditionCount;
const getColumnByFieldId = (state, fieldId) => {
    const columns = (0, exports.getVisibleColumns)(state);
    return columns.find(column => column.fieldId === fieldId);
};
exports.getColumnByFieldId = getColumnByFieldId;
const getRowHeightFromLevel = (level) => {
    return level == null ? constants_1.RowHeight.Short : constants_1.RowHeight[constants_1.RowHeightLevel[level]];
};
exports.getRowHeightFromLevel = getRowHeightFromLevel;
exports.getGroupLevel = (0, reselect_1.createSelector)([exports.getActiveViewGroupInfo], groupInfo => {
    return groupInfo.length;
});
exports.getCurrentGalleryViewStyle = (0, reselect_1.createSelector)([exports.getCurrentView], (view) => {
    if (view.type !== constants_1.ViewType.Gallery) {
        return;
    }
    return view.style;
});
const getIntegratePermissionWithFieldBase = (_state, { permission, fieldId, fieldPermissionMap }) => {
    const fieldPermission = fieldPermissionMap && fieldId ? fieldPermissionMap[fieldId] : undefined;
    if (!fieldPermission) {
        return permission;
    }
    return Object.assign(Object.assign({}, permission), { fieldRemovable: fieldPermission.manageable, cellEditable: fieldPermission.role === interfaces_1.Role.Editor, fieldPropertyEditable: fieldPermission.manageable });
};
const getIntegratePermissionWithField = (0, re_reselect_1.default)(getIntegratePermissionWithFieldBase, permission => {
    return permission;
})({
    keySelector: (state, { datasheetId, mirrorId }) => mirrorId || datasheetId || (0, base_1.getActiveDatasheetId)(state),
    selectorCreator: exports.createDeepEqualSelector,
});
const getPermissions = (state, datasheetId, fieldId, sourceMirrorId) => {
    var _a, _b, _c, _d, _e;
    const datasheet = (0, base_1.getDatasheet)(state, datasheetId);
    const mirrorId = sourceMirrorId || state.pageParams.mirrorId;
    const nodeConnected = mirrorId ? (_a = (0, mirror_1.getMirrorNetworking)(state, mirrorId)) === null || _a === void 0 ? void 0 : _a.connected : (0, base_1.getDatasheetConnected)(state, datasheetId);
    const screenWidth = (_b = state === null || state === void 0 ? void 0 : state.space) === null || _b === void 0 ? void 0 : _b.screenWidth;
    const paramsNodeId = mirrorId || (0, base_1.getActiveDatasheetId)(state);
    const fieldPermissionMap = (0, base_1.getFieldPermissionMap)(state, datasheetId);
    // only in the mirror, the permission should be integrated with field permission
    // which means the url of datasheetId should be the same as "the datasheetId to query"
    const nodePermission = mirrorId && ((datasheet === null || datasheet === void 0 ? void 0 : datasheet.id) === (0, base_1.getActiveDatasheetId)(state) || sourceMirrorId) ? (_c = (0, mirror_1.getMirror)(state, mirrorId)) === null || _c === void 0 ? void 0 : _c.permissions : datasheet === null || datasheet === void 0 ? void 0 : datasheet.permissions;
    const blackSpace = (_e = (_d = state.billing) === null || _d === void 0 ? void 0 : _d.subscription) === null || _e === void 0 ? void 0 : _e.blackSpace;
    if (blackSpace) {
        // blacklist space will reset all node's permission to readonly
        return constants_1.DEFAULT_PERMISSION;
    }
    if (screenWidth && screenWidth < constants_1.ScreenWidth.md) {
        // smallScreen temporary not allow edit
        // TODO: mobile will support edit in the future
        const permission = datasheet
            ? getIntegratePermissionWithField(state, {
                permission: engine_1.ViewPropertyFilter.getReaderRolePermission(state, datasheet.id, nodePermission),
                datasheetId,
                fieldPermissionMap,
                fieldId: fieldId,
                mirrorId,
            })
            : {};
        return Object.assign(Object.assign(Object.assign({}, constants_1.DEFAULT_PERMISSION), permission), { rowSortable: false });
    }
    // forbid all permission when previewing time machine
    if (!datasheet || state.datasheetMap[constants_1.PREVIEW_DATASHEET_ID]) {
        return constants_1.DEFAULT_PERMISSION;
    }
    // share / templates page, return permission directly
    if (state.pageParams.shareId || state.pageParams.templateId || state.pageParams.embedId) {
        return getIntegratePermissionWithField(state, {
            permission: engine_1.ViewPropertyFilter.getReaderRolePermission(state, datasheet.id, nodePermission),
            datasheetId,
            fieldPermissionMap,
            fieldId: fieldId,
            mirrorId,
        });
    }
    /**
     * when connection is not ready, return default forbidden edit permission
     * but, here attention, according to v0.6.2 infra refactor,
     * the whole `tab` only exist one room.
     * in the other world, only 1 datasheet under connect state.
     * for relation datasheets, connect is false,
     * so, the check of connect, only in the main datasheet of creating room.
     * no need to care about the relation datasheets.
     *
     */
    if (!nodeConnected && paramsNodeId === datasheet.id) {
        return constants_1.DEFAULT_PERMISSION;
    }
    return getIntegratePermissionWithField(state, {
        permission: engine_1.ViewPropertyFilter.getReaderRolePermission(state, datasheet.id, nodePermission),
        datasheetId,
        fieldPermissionMap,
        fieldId: fieldId,
        mirrorId,
    });
};
exports.getPermissions = getPermissions;
const getViewRowHeight = (state) => {
    const view = (0, exports.getCurrentView)(state);
    if ([constants_1.ViewType.Grid, constants_1.ViewType.Gantt].includes(view === null || view === void 0 ? void 0 : view.type)) {
        // new table has no rowHeight property, only add view will init new view.
        return view.rowHeightLevel || constants_1.RowHeightLevel.Short;
    }
    return;
};
exports.getViewRowHeight = getViewRowHeight;
// get view's actual row count, not affected by filter
const getActualRowCount = (state) => {
    const view = (0, exports.getCurrentView)(state);
    return view === null || view === void 0 ? void 0 : view.rows.length;
};
exports.getActualRowCount = getActualRowCount;
const getFilterConditionValue = (state, conditionId) => {
    const filterInfo = (0, exports.getCurrentView)(state).filterInfo;
    if (filterInfo) {
        const result = filterInfo.conditions.find(item => {
            return item.conditionId === conditionId;
        });
        return result && result.value ? result.value : null;
    }
    return null;
};
exports.getFilterConditionValue = getFilterConditionValue;
exports.getKanbanGroupMapIds = (0, reselect_1.createSelector)([exports.getFieldMap, exports.getKanbanFieldId, base_1.getFieldPermissionMap], (fieldMap, kanbanFieldId, fieldPermissionMap) => {
    if (!kanbanFieldId) {
        return [];
    }
    const fieldRole = (0, base_1.getFieldRoleByFieldId)(fieldPermissionMap, kanbanFieldId);
    if (fieldRole === interfaces_1.Role.None) {
        return [];
    }
    const field = fieldMap[kanbanFieldId];
    if (!field) {
        return [];
    }
    if (field.type === types_1.FieldType.SingleSelect) {
        return field.property.options.map(item => item.id);
    }
    return field.property.unitIds;
});
exports.getQueryMeta = (0, reselect_1.createSelector)([exports.getVisibleColumns, base_1.getActiveDatasheetId, exports.getActiveViewFilterInfo, exports.getActiveViewGroupInfo, exports.getActiveViewSortInfo], (selectFields, fromDstId, filterInfo, groupInfo, sortInfo) => {
    let q = `select ${selectFields.map(field => field.fieldId).join(',')} from ${fromDstId}\n`;
    if (filterInfo) {
        q += `where ${filterInfo.conditions.map(cond => `${cond.fieldId} ${cond.operator} ${cond.value}`).join(` ${filterInfo.conjunction} `)}\n`;
    }
    if (groupInfo && groupInfo.length) {
        q += `group by ${groupInfo.map(gInfo => gInfo.fieldId).join(',')}\n`;
    }
    if (sortInfo) {
        q += `order by ${sortInfo.rules.map(rule => `${rule.fieldId} ${rule.desc ? 'desc' : ''}`).join(',')}\n`;
    }
    return q;
});
const getCalendarStyle = (state) => {
    const view = (0, exports.getCurrentView)(state);
    if (!view || view.type !== constants_1.ViewType.Calendar) {
        return;
    }
    return view.style;
};
exports.getCalendarStyle = getCalendarStyle;
const getCalendarViewStatus = (state, datasheetId) => {
    const client = (0, base_1.getDatasheetClient)(state, datasheetId);
    if (!client) {
        return client;
    }
    return client.calendarViewStatus;
};
exports.getCalendarViewStatus = getCalendarViewStatus;
const getOrgChartViewStatus = (state, datasheetId) => {
    const client = (0, base_1.getDatasheetClient)(state, datasheetId);
    if (!client) {
        return client;
    }
    return client.orgChartViewStatus;
};
exports.getOrgChartViewStatus = getOrgChartViewStatus;
const getKanbanViewStatus = (state, datasheetId) => {
    const client = (0, base_1.getDatasheetClient)(state, datasheetId);
    if (!client) {
        return client;
    }
    return client.kanbanViewStatus;
};
exports.getKanbanViewStatus = getKanbanViewStatus;
exports.getCalendarVisibleColumns = (0, re_reselect_1.default)([exports.getCurrentView], (view) => {
    return view ? view.columns.filter((item, i) => !(item.hiddenInCalendar && i !== 0)) : [];
})(defaultKeySelector);
exports.getOrgChartVisibleColumns = (0, re_reselect_1.default)([exports.getCurrentView], (view) => {
    return view ? view.columns.filter((item, i) => !(item.hiddenInOrgChart && i !== 0)) : [];
})(defaultKeySelector);
const getCalendarVisibleColumnCount = (state) => {
    const view = (0, exports.getCurrentView)(state);
    if (!view || view.type !== constants_1.ViewType.Calendar) {
        return -1;
    }
    return (0, exports.getCalendarVisibleColumns)(state).length;
};
exports.getCalendarVisibleColumnCount = getCalendarVisibleColumnCount;
const getGanttStyle = (state) => {
    const view = (0, exports.getCurrentView)(state);
    if (!view || view.type !== constants_1.ViewType.Gantt) {
        return;
    }
    return view.style;
};
exports.getGanttStyle = getGanttStyle;
exports.getGanttVisibleColumns = (0, re_reselect_1.default)([exports.getCurrentView], (view) => {
    return view ? view.columns.filter(item => !item.hiddenInGantt) : [];
})(defaultKeySelector);
const getGanttVisibleColumnCount = (state) => {
    const view = (0, exports.getCurrentView)(state);
    if (!view || view.type !== constants_1.ViewType.Gantt) {
        return -1;
    }
    return (0, exports.getGanttVisibleColumns)(state).length;
};
exports.getGanttVisibleColumnCount = getGanttVisibleColumnCount;
const integrateRecordEditable = (state, fieldId, datasheetId) => {
    const permission = (0, exports.getPermissions)(state, datasheetId);
    const fieldPermissionMap = (0, base_1.getFieldPermissionMap)(state, datasheetId);
    const fieldRole = (0, base_1.getFieldRoleByFieldId)(fieldPermissionMap, fieldId);
    if (fieldRole) {
        return fieldRole === interfaces_1.Role.Editor;
    }
    return permission.editable;
};
exports.integrateRecordEditable = integrateRecordEditable;
const getDateTimeCellAlarm = (snapshot, recordId, fieldId) => {
    var _a, _b, _c;
    // notification center open card without snapshot
    const recordMeta = (_b = (_a = snapshot === null || snapshot === void 0 ? void 0 : snapshot.recordMap) === null || _a === void 0 ? void 0 : _a[recordId]) === null || _b === void 0 ? void 0 : _b.recordMeta;
    if (!recordMeta) {
        return;
    }
    const fieldExtraMap = recordMeta === null || recordMeta === void 0 ? void 0 : recordMeta.fieldExtraMap;
    if (!fieldExtraMap) {
        return;
    }
    return (_c = fieldExtraMap[fieldId]) === null || _c === void 0 ? void 0 : _c.alarm;
};
exports.getDateTimeCellAlarm = getDateTimeCellAlarm;
/**
 *
 * a method for front-end use
 * attention: this method return a new data structure, can not be used in useSelector
 *
 */
const getDateTimeCellAlarmForClient = (snapshot, recordId, fieldId) => {
    const alarm = (0, exports.getDateTimeCellAlarm)(snapshot, recordId, fieldId);
    if (!alarm) {
        return;
    }
    if (!Array.isArray(alarm.alarmUsers)) {
        return;
    }
    if (alarm.alarmUsers[0].type === interfaces_1.AlarmUsersType.Field) {
        const fieldMap = snapshot.meta.fieldMap;
        return Object.assign(Object.assign({}, alarm), { target: interfaces_1.AlarmUsersType.Field, alarmUsers: alarm.alarmUsers.map(item => item.data).filter(fieldId => fieldMap[fieldId] && fieldMap[fieldId].type === types_1.FieldType.Member) });
    }
    return Object.assign(Object.assign({}, alarm), { target: interfaces_1.AlarmUsersType.Member, alarmUsers: alarm.alarmUsers.map(item => item.data) });
};
exports.getDateTimeCellAlarmForClient = getDateTimeCellAlarmForClient;
//# sourceMappingURL=calc.js.map